import type { Translations } from './index.js';

/**
 * German UI strings.
 *
 * Two conventions worth knowing, because they run through the whole file:
 *
 * - The app addresses people informally ("du"). It is a tool for an evening
 *   with friends, not a bank, and "Sie" would sit oddly next to a screen that
 *   asks whether you are fine with a film.
 * - Product and brand names stay English — "Trailer", "Streaming", the
 *   provider names. Translating those would read as stilted, not as German.
 */
export const de: Translations = {
  'app.name': 'FilmSloth',
  'app.tagline': 'Entscheidet gemeinsam, was ihr heute Abend schaut. Ganz entspannt.',

  'common.back': 'Zurück',
  'common.cancel': 'Abbrechen',
  'common.continue': 'Weiter',
  'common.loading': 'Lädt…',
  'common.retry': 'Nochmal versuchen',
  'common.close': 'Schließen',
  'common.copy': 'Kopieren',
  'common.copied': 'Kopiert',
  'common.minutes': '{count} Min.',
  'common.host': 'Gastgeber',
  'common.you': 'du',
  'common.language': 'Sprache',
  'common.anyGenre': 'Alle Genres',
  'common.withoutGenres': 'Kein {genres}',
  'common.anyYear': 'Egal wann',
  'common.fromYear': 'Ab {year}',
  'common.anyRating': 'Egal wie bewertet',
  'common.fromRating': '★ {rating}+',
  'common.noLimit': 'Kein Limit',

  'home.create': 'Runde starten',
  'home.createHint': 'Dienste auswählen, Freunde einladen, losswipen.',
  'home.join': 'Runde beitreten',
  'home.joinHint': 'Du hast einen Code bekommen? Hier eingeben.',

  'create.title': 'Neue Runde',
  'create.yourName': 'Dein Name',
  'create.namePlaceholder': 'z. B. Alex',
  'create.providers': 'Streaming-Dienste',
  'create.providersHint': 'Es tauchen nur Filme auf, die es bei diesen Diensten gibt.',
  'create.genres': 'Genres',
  'create.genresHint': 'Leer lassen für alles.',
  'create.excludedGenres': 'Bloß nicht',
  'create.excludedGenresHint': 'Alles mit einem dieser Genres bleibt draußen.',
  'create.runtime': 'Maximale Länge',
  'create.minYear': 'Erschienen',
  'create.tryWithoutYear': 'Ohne die Jahresgrenze wären es {count}.',
  'create.minRating': 'TMDB-Bewertung',
  'create.minRatingHint':
    'Filme mit zu wenigen Stimmen haben noch keine Bewertung und fallen mit einer Mindestbewertung raus.',
  'create.tryWithoutRating': 'Ohne die Mindestbewertung wären es {count}.',
  'create.submit': 'Runde erstellen',
  'create.needName': 'Gib deinen Namen ein, um weiterzumachen.',
  'create.needProvider': 'Wähle mindestens einen Streaming-Dienst.',
  'create.matchingMovies': '{count} Filme passen',
  'create.noMovies': 'Zu diesen Filtern passt noch kein Film.',
  'create.tryWithoutGenres': 'Ohne den Genre-Filter wären es {count}.',
  'create.tryWithoutRuntime': 'Ohne die Längenbegrenzung wären es {count}.',
  'create.tryMoreProviders': 'Nimm noch einen Streaming-Dienst dazu.',

  'join.title': 'Runde beitreten',
  'join.code': 'Code der Runde',
  'join.codePlaceholder': 'ABC123',
  'join.yourName': 'Dein Name',
  'join.submit': 'Beitreten',
  'join.hostedBy': 'Gastgeber: {name}',
  'join.peopleInside': '{count} in der Runde',
  'join.notFound': 'Zu diesem Code gibt es keine Runde.',
  'join.scan': 'QR-Code scannen',
  'join.scanHint': 'Halte die Kamera auf den Code auf dem anderen Handy.',
  'join.scanDenied': 'Keine Kamera da. Erlaube die Kamera oder tipp den Code oben ein.',
  'join.scanUnavailable': 'Dieser Browser kann nicht scannen. Tipp den Code oben ein.',

  'lobby.title': 'Wartezimmer',
  'lobby.shareCode': 'Diesen Code teilen',
  'lobby.shareLink': 'Link kopieren',
  'lobby.inviteLink': 'Einladungslink',
  'lobby.qrLabel': 'QR-Code mit dem Einladungslink zur Runde {code}',
  'lobby.qrHint': 'Alle im Raum können einfach ihre Kamera darauf halten.',
  'lobby.copyFailed':
    'Kopieren ist hier blockiert — markiere den Link oben und kopiere ihn von Hand.',
  'lobby.localhostHint':
    'Du hast die App über localhost geöffnet, dieser Link funktioniert also nur auf diesem Rechner. Um Leute aus deinem WLAN einzuladen, nimm stattdessen die Netzwerkadresse, die der Dev-Server ausgibt.',
  'lobby.people': 'In der Runde',
  'lobby.filters': 'Woraus ihr auswählt',
  'lobby.deckSize': '{count} Filme im Stapel',
  'lobby.start': 'Losswipen',
  'lobby.waitingForHost': 'Warten auf {name}…',
  'lobby.invite': 'Freunde einladen',
  'lobby.copyCode': 'Code kopieren',
  'lobby.inviteTitle': 'FilmSloth',
  'lobby.inviteText':
    'Mach bei meiner FilmSloth-Runde mit und such den Film für heute Abend aus. Code: {code}',
  'lobby.aloneHint':
    'Noch niemand sonst da — du kannst trotzdem starten, andere kommen später dazu.',
  'lobby.inviteMore': 'Weitere Leute einladen',
  'lobby.editFilters': 'Filter ändern',

  'swipe.like': 'Ja',
  'swipe.neutral': 'Egal',
  'swipe.dislike': 'Nein',
  'swipe.progress': '{voted} von {total} bewertet',
  'swipe.emptyTitle': 'Das war alles',
  'swipe.emptyBody':
    'Du hast jeden Film im Stapel bewertet. Lehn dich zurück — jetzt sind die anderen dran.',
  'swipe.results': 'Ergebnisse',
  'swipe.leave': 'Runde verlassen',
  'swipe.finishSession': 'Runde beenden',
  'swipe.finishConfirm': 'Für alle beenden?',
  'swipe.leaveShort': 'Verlassen',
  'swipe.leaveConfirm': 'Wirklich verlassen?',
  'swipe.undo': 'Zurücknehmen',
  'swipe.later': 'Später fragen',
  'swipe.alreadySeen': 'Schon gesehen',
  'swipe.keyboardHint': 'Tipp: Karte wischen oder ← ↓ → auf der Tastatur. ↑ fragt später noch mal.',

  'trailer.play': 'Trailer abspielen',
  'trailer.label': 'Trailer zu {title}',
  'trailer.consentNote':
    'Beim Abspielen wird das Video von YouTube geladen, dabei geht deine IP-Adresse an Google.',
  'trailer.disable': 'Trailer ausschalten',
  'trailer.community': 'Von der Community ergänzt',

  'trailerReport.action': 'Falscher Trailer?',
  'trailerReport.busy': 'Wird gemeldet…',
  'trailerReport.hidden': 'Für diese Runde ausgeblendet. Danke für den Hinweis!',
  'trailerReport.counted':
    'Hier ausgeblendet und notiert. Wenn ein paar weitere Gruppen dasselbe sagen, fliegt er für alle raus.',
  'trailerReport.removed': 'Für alle entfernt. Jetzt kann jemand den richtigen ergänzen.',
  'trailerReport.failed': 'Das hat nicht geklappt. Bitte versuch es gleich nochmal.',

  'trailerAdd.open': 'Du kennst den Trailer? Ergänze ihn',
  'trailerAdd.title': 'Trailer zu {title} ergänzen',
  'trailerAdd.hint':
    'Füge den YouTube-Link ein. Wir nehmen nur Trailer von offiziellen Studio- und Verleih-Kanälen, und wir prüfen, ob das Video wirklich der Trailer zu diesem Film ist.',
  'trailerAdd.placeholder': 'https://www.youtube.com/watch?v=...',
  'trailerAdd.submit': 'Trailer ergänzen',
  'trailerAdd.submitting': 'Wird geprüft…',
  'trailerAdd.cancel': 'Abbrechen',
  'trailerAdd.success': 'Danke! Der Trailer ist jetzt für alle in dieser Runde da.',

  'trailerError.not_youtube':
    'Das ist kein YouTube-Link. Nimm einen Link von youtube.com oder youtu.be.',
  'trailerError.trailer_broken':
    'Laut YouTube ist das Video nicht verfügbar oder darf hier nicht eingebettet werden. Versuch einen anderen Link.',
  'trailerError.trailer_unverifiable':
    'Wir konnten YouTube nicht erreichen, um das Video zu prüfen. Bitte gleich nochmal versuchen.',
  'trailerError.channel_not_official':
    'Das Video stammt nicht von einem offiziellen Studio- oder Verleih-Kanal. Wir nehmen Trailer nur vom Studio selbst, damit klar ist, dass sie hier gezeigt werden dürfen.',
  'trailerError.not_a_trailer':
    'Das Video sieht nicht nach einem Trailer aus. Such nach dem offiziellen Trailer oder Teaser.',
  'trailerError.title_mismatch': 'Der Trailer scheint zu einem anderen Film zu gehören.',
  'trailerError.year_mismatch':
    'Der Trailer scheint zu einer anderen Fassung dieses Films zu gehören.',
  'trailerError.trailer_exists': 'Gerade hat jemand anderes einen Trailer für diesen Film ergänzt.',
  'trailerError.rate_limited':
    'Danke — wir freuen uns wirklich über deine Hilfe bei den Trailern! Damit hier nichts aus dem Ruder läuft, begrenzen wir, wie viele Änderungen auf einmal möglich sind. Bitte versuch es etwas später nochmal.',
  'trailerError.unknown':
    'Dieser Trailer ließ sich nicht ergänzen. Versuch bitte einen anderen Link.',

  'match.title': 'Das ist ein Match!',
  'match.body': 'Damit sind alle einverstanden.',
  'match.hostWatch': 'Den schauen wir',
  'match.hostContinue': 'Weitersuchen',
  'match.waitingForHost': '{name} entscheidet gerade, ob ihr weitersucht…',

  'results.title': 'Zwischenstand',
  'results.subtitle': 'Ja +1 · Egal 0 · Nein −1',
  'results.empty': 'Bisher hat noch niemand etwas bewertet.',
  'results.points': '{count} Pkt.',
  'results.votes': '{likes} Ja · {neutrals} Egal · {dislikes} Nein',
  'results.votersNo': 'Nein: {names}',
  'results.votersYes': 'Ja: {names}',
  'results.votersNeutral': 'Egal: {names}',
  'results.matched': 'Match',
  'results.complete': 'Alle haben abgestimmt',

  'watchOn.title': 'Wo du ihn sehen kannst',
  'watchOn.searchHint': 'Bei {provider} nach „{title}“ suchen',
  'watchOn.homeHint': '{provider} öffnen — für diesen Anbieter haben wir keinen Suchlink',
  'taste.favourites': 'Eure Runde mag am liebsten {genres}.',
  'taste.avoided': 'Und macht einen Bogen um {genres}.',

  'ranking.share': 'Top fünf teilen',
  'ranking.shareHeader': 'Unser FilmSloth-Ranking:',

  'share.action': 'Ergebnis teilen',
  'share.copied': 'In die Zwischenablage kopiert',
  'share.failed': 'Teilen hat nicht geklappt',
  'share.subject': 'Heute Abend schauen wir',
  'share.body': 'Heute Abend schauen wir {title} ({year}). Ausgesucht mit FilmSloth.',
  'share.bodyNoYear': 'Heute Abend schauen wir {title}. Ausgesucht mit FilmSloth.',

  'group.title': 'Die Runde ({count})',
  'group.rated': '{count} bewertet',
  'group.ratedDone': '{count} bewertet · durch den Stapel',
  'group.stuckHint':
    'Für ein Match braucht es eine Stimme von allen. Wer nicht mehr wischt, kann vom Host entfernt werden.',
  'group.makeHost': 'Zum Host machen',
  'group.remove': 'Entfernen',
  'group.removeLabel': '{name} aus der Runde entfernen',
  'group.removeConfirm': '{name} entfernen?',
  'group.removedBanner':
    'Der Host hat dich aus der Runde genommen. Mit dem Einladungslink kommst du wieder rein.',

  'matchRule.label': 'Wann ist es ein Match?',
  'matchRule.lenient': 'Keiner hat was dagegen',
  'matchRule.strict': 'Alle wollen ihn',
  'matchRule.lenientHint': 'Alle haben abgestimmt, niemand hat Nein gesagt, mindestens einer Ja.',
  'matchRule.strictHint': 'Nur wenn wirklich jede Stimme ein Ja ist.',

  'timer.label': 'Zeitlimit',
  'timer.enable': 'Der Runde ein Zeitlimit geben',
  'timer.minutesLabel': 'Minuten',
  'timer.minutesUnit': 'Minuten',
  'timer.hint':
    'Ist die Zeit um, entscheidet die Stichwahl — vorher kannst du jederzeit nachlegen.',
  'timer.summary': 'Zeitlimit: {count} Min.',
  'timer.offSummary': 'Kein Zeitlimit',
  'timer.untilRunoff': 'bis zur Stichwahl',
  'timer.upTitle': 'Die Zeit ist um',
  'timer.upBody': 'Die Stichwahl startet in {time}.',
  'timer.upBodyNothing':
    'Für eine engere Auswahl ist noch zu wenig abgestimmt. Leg Zeit nach oder swipt weiter.',
  'timer.upWaiting': '{name} entscheidet, ob es Zeit dazugibt.',
  'timer.add': '{count} Min. dazugeben',
  'timer.addLabel': 'Minuten dazugeben',
  'timer.off': 'Zeitlimit aufheben',

  'runoff.offer':
    'Alle Karten sind abgestimmt und nichts hat gematcht. Eine engere Auswahl gibt es trotzdem.',
  'runoff.offerWaiting': 'Der Host kann die engere Auswahl zur Wahl stellen.',
  'runoff.start': 'Zur Wahl stellen',
  'runoff.title': 'Stichwahl',
  'runoff.body': 'Eine Stimme pro Person, änderbar bis alle abgestimmt haben.',
  'runoff.outstanding': 'Warten auf noch {count}.',
  'runoff.allIn': 'Alle haben abgestimmt.',
  'runoff.tieHost': 'Gleichstand. Du entscheidest.',
  'runoff.tieWaiting': 'Gleichstand — der Host entscheidet.',
  'runoff.changeHint': 'Tipp auf einen anderen Titel, um deine Stimme zu ändern.',
  'runoff.votes': '{count} Stimmen',

  'error.offline': 'Du bist offline. Bis die Verbindung zurück ist, geht nichts durch.',

  'finished.title': 'Heute Abend schaut ihr',
  'finished.noPick': 'Die Runde wurde ohne Ergebnis beendet.',
  'finished.viewResults': 'Vollständige Rangliste ansehen',
  'finished.nextRound': 'Noch eine Runde, gleiche Leute',
  'finished.joinNextRound': 'Zur nächsten Runde',
  'finished.nextRoundStarted': 'Jemand hat eine neue Runde mit denselben Filtern gestartet.',
  'finished.newSession': 'Neue Runde starten',

  'finished.watchNow': 'Jetzt auf {provider} schauen',
  'finished.alsoOn': 'Auch auf:',
  'finished.details': 'Details & Trailer',

  'finished.tipHeadlineSaved': '{minutes} gespart',
  'finished.tipHeadlineSlow': '{duration} gebraucht',
  'finished.donateBodySaved':
    'Ihr habt in {duration} entschieden, deutlich unter den üblichen {average}. Das sind {minutes} eures Lebens gerettet.',
  'finished.donateBodySlow':
    'Das hat {duration} gedauert, etwas mehr als die üblichen {average}. Kein Drama — Entscheiden ist schwer.',
  'finished.tipLabel': 'Trinkgeld für den Faultier-Butler',
  'finished.tipOther': 'Anderer Betrag',

  'error.generic': 'Da ist etwas schiefgelaufen.',
  'error.connectionLost': 'Verbindung verloren. Wird neu aufgebaut…',
  'swipe.pendingVotes':
    'Noch nicht abgeschickt: {count}. Geht von selbst raus, sobald die Verbindung wieder da ist.',
  'error.sessionGone': 'Diese Runde gibt es nicht mehr.',

  'rating.label': 'Deine Bewertung',
  'rating.score': '{score} von {max}',
  'rating.community': '{score} aus {count} Bewertungen hier',
  'rating.provisional': 'noch im Einpendeln',
  'rating.none': 'Hier hat das noch niemand bewertet.',
  'rating.clear': 'Bewertung entfernen',
  'rating.prompt': 'Wie war er?',

  'watchlist.title': 'Deine Filme',
  'watchlist.open': 'Deine Filme',
  'watchlist.save': 'Merken',
  'watchlist.saved': 'Gemerkt',
  'watchlist.tabSaved': 'Für später gemerkt',
  'watchlist.tabWatched': 'Zusammen geschaut',
  'watchlist.emptySaved':
    'Noch nichts gemerkt. Tipp auf den Stern einer Karte, um einen Film festzuhalten.',
  'watchlist.emptyWatched': 'Bisher hat noch kein Film eine Runde gewonnen.',
  'watchlist.deviceNote':
    'Diese Liste liegt auf diesem Gerät. Wer seine Cookies löscht, löscht sie mit.',

  'movieSuggestion.suggest': 'Film vorschlagen',
  'movieSuggestion.suggested': 'Vorschlag eingereicht',
  'movieSuggestion.title': 'Film vorschlagen',
  'movieSuggestion.hint': 'Trag nur ein, was du sicher weißt. Der Film taucht sofort für alle auf.',
  'movieSuggestion.movieName': 'Filmtitel',
  'movieSuggestion.year': 'Erscheinungsjahr',
  'movieSuggestion.runtimeHint': 'z. B. 136 für 2 Std. 16 Min.',
  'movieSuggestion.namePlaceholder': 'z. B. The Matrix',
  'movie.community': 'Von der Community vorgeschlagen',
  'movie.providersSource': 'Streaming-Daten: JustWatch',
  'movie.ratingVotes': 'TMDB-Bewertung aus {count} Stimmen',
  'movieSuggestion.runtime': 'Länge (Minuten)',
  'movieSuggestion.genres': 'Genres (optional)',
  'movieSuggestion.providers': 'Streaming-Dienste (optional)',
  'movieSuggestion.submit': 'Vorschlag absenden',
  'movieSuggestion.submitting': 'Wird gesendet…',
  'movieSuggestion.cancel': 'Abbrechen',
  'movieSuggestion.success': 'Film ergänzt!',

  // Auth: creating an account is optional almost everywhere. It only starts
  // paying off once you contribute to the Filmwerkstatt or want ad-freedom.
  'auth.title': 'Anmelden oder registrieren',
  'auth.subtitle':
    'Ein Konto brauchst du nur, um in der Filmwerkstatt mitzumachen oder werbefrei zu schauen. Alles andere geht auch so.',
  'auth.login': 'Anmelden',
  'auth.register': 'Registrieren',
  'auth.email': 'E-Mail',
  'auth.emailOrUsername': 'E-Mail oder Benutzername',
  'auth.username': 'Benutzername',
  'auth.usernameHint': '3–32 Zeichen: Buchstaben, Zahlen, „_“ und „-“.',
  'auth.password': 'Passwort',
  'auth.submitLogin': 'Anmelden',
  'auth.submitRegister': 'Konto erstellen',
  'auth.passwordHint': 'Mindestens 10 Zeichen.',
  'auth.forgot': 'Passwort vergessen?',
  'auth.forgotSent': 'Falls es dieses Konto gibt, haben wir dir eine Mail geschickt.',
  'auth.busy': 'Wird geprüft…',
  'auth.carriesOver': 'Das nimmst du mit',
  'auth.carriesWatchlist': 'Deine gemerkten Filme',
  'auth.carriesRatings': 'Deine Bewertungen',
  'auth.carriesFilms': 'Filme, die du vorgeschlagen hast',
  'auth.carriesNote': 'Das gilt nur für dieses Gerät.',
  'auth.errorCredentials': 'E-Mail, Benutzername oder Passwort stimmt nicht.',
  'auth.errorDuplicate': 'Für diese E-Mail gibt es schon ein Konto.',
  'auth.errorDuplicateUsername': 'Dieser Benutzername ist schon vergeben.',
  'auth.errorPasswordShort': 'Das Passwort braucht mindestens 10 Zeichen.',
  'auth.errorEmailInvalid': 'Das sieht nicht nach einer gültigen E-Mail-Adresse aus.',
  'auth.errorUsernameInvalid': 'Benutzername: 3–32 Zeichen, nur Buchstaben, Zahlen, "_" und "-".',
  'auth.errorGeneric': 'Da ist etwas schiefgelaufen.',
  'auth.errorTooMany': 'Zu viele Versuche. Bitte versuch es später nochmal.',

  // Account: what an account is worth, and the ledger behind it.
  'account.title': 'Konto',
  'account.confirmed': 'E-Mail bestätigt',
  'account.unconfirmed': 'E-Mail noch nicht bestätigt',
  'account.adFreeUntil': 'Werbefrei bis {date}',
  'account.adFreeNone': 'Noch kein Werbefrei-Guthaben.',
  'account.daysLeft': '{days} Tage übrig',
  'account.perContribution': 'Bestätigter Beitrag: 3 Tage. Ausgefüllte Lücke: 6 Tage.',
  'account.cap': 'Maximal 90 Tage auf einmal.',
  'account.confirmedContributions': 'Bestätigte Beiträge',
  'account.openContributions': 'Offene Beiträge',
  'account.payoutNote':
    'Gutschrift gibt es erst, wenn ein Fakt entschieden ist — und nur für die Antwort, die gewinnt. Nicht fürs bloße Mitmachen.',
  'account.ledger': 'Verlauf',
  'account.ledgerEmpty': 'Noch keine Einträge.',
  'account.logout': 'Abmelden',
  'account.reasonFill': 'Lücke gefüllt',
  'account.reasonConfirm': 'Beitrag bestätigt',
  'account.seenSettingsTitle': 'Schon gesehene Filme',
  'account.seenSettingsHint':
    'Wenn du beim Swipen einen Film als schon gesehen markierst, bleibt er für diese Dauer aus deinem Stapel in einer Session.',
  'account.seenExclusionDays': '{days} Tage',
  'account.seenExclusionForever': 'Für immer',
  'account.preferencesTitle': 'Standardauswahl für neue Runden',
  'account.preferencesHint':
    'Wähle deine Streaming-Dienste und bevorzugten Genres — eine neue Runde startet direkt mit dieser Auswahl.',
  'account.languageHint':
    'FilmSloth ist mit der Sprache deines Browsers gestartet. Falls das nicht passt, änderst du es hier.',

  'account.statsTitle': 'Nutzung',
  'account.statsHint':
    'Wie viel die Seite benutzt wird — Seitenaufrufe, Runden, und ob die Leute über die installierte App oder einen Browser kommen. Nur für Moderatoren.',
  'account.statsOpen': 'Nutzungszahlen öffnen',

  // Das Instrumentenbrett des Betreibers. Die Namen der Zähler stehen dort so,
  // wie sie gespeichert sind, und werden nicht übersetzt — Begründung in
  // StatsScreen.tsx.
  'stats.title': 'Nutzung',
  'stats.intro':
    'Aggregierte Zähler: eine Summe pro Tag, in der nichts steht, was jemanden identifiziert. Aktionen zählt der Server, wenn sie geklappt haben; Seitenaufrufe meldet die Seite selbst und lässt sich blocken — die sind also eine Untergrenze und keine Messung.',
  'stats.range': 'Zeitraum',
  'stats.rangeDays': 'Letzte {days} Tage',
  'stats.forbidden': 'Diese Zahlen sind für Moderatoren, und dieses Konto ist keiner.',
  'stats.failed': 'Die Zahlen ließen sich nicht laden.',
  'tmdb.title': 'Filmkatalog (TMDB)',
  'tmdb.reload': 'Neu laden',
  'tmdb.loadFailed': 'Der Sync-Status ließ sich nicht laden.',
  'tmdb.disabled':
    'TMDB ist auf dem Server nicht eingerichtet (TMDB_READ_TOKEN fehlt). Das Deck kommt aus der mitgelieferten Filmliste.',
  'tmdb.running': 'Ein Sync läuft gerade …',
  'tmdb.fallback':
    'Der TMDB-Katalog ist leer, deshalb kommt das Deck gerade aus der mitgelieferten Filmliste.',
  'tmdb.films': 'Filme im Deck',
  'tmdb.due': 'Zur Aktualisierung fällig',
  'tmdb.withoutDetails': '{count} noch nie abgerufen',
  'tmdb.oldest': 'Älteste Daten im Deck',
  'tmdb.next': 'Nächster geplanter Lauf',
  'tmdb.settings':
    'Täglich: bis zu {perRun} Filme aktualisiert, {pages} Discover-Seiten pro Dienst. Daten gelten nach {days} Tagen als veraltet; geprüft wird alle {hours} Std.',
  'tmdb.runsTitle': 'Letzte Durchläufe',
  'tmdb.colStarted': 'Gestartet',
  'tmdb.colStatus': 'Status',
  'tmdb.colTrigger': 'Ausgelöst',
  'tmdb.colDuration': 'Dauer',
  'tmdb.colDiscovered': 'Gefunden',
  'tmdb.colRefreshed': 'Aktualisiert / fällig',
  'tmdb.colGone': 'Entfernt',
  'tmdb.noRuns': 'Noch keine Durchläufe.',
  'tmdb.status.ok': 'OK',
  'tmdb.status.failed': 'Fehlgeschlagen',
  'tmdb.status.running': 'Läuft',
  'tmdb.trigger.scheduled': 'Zeitplan',
  'tmdb.trigger.manual': 'Manuell',
  'tmdb.refreshedOf': '{done} / {due}',
  'tmdb.manualTitle': 'Sync jetzt starten',
  'tmdb.manualHint':
    'Aktualisiert höchstens die angegebene Zahl an Filmen, noch nie abgerufene zuerst, danach die ältesten. Ein manueller Lauf verschiebt den täglichen Zeitplan nicht.',
  'tmdb.detailsLimit': 'Höchstens so viele Filme aktualisieren',
  'tmdb.discover': 'Vorher nach neuen Filmen suchen (Discover)',
  'tmdb.discoverPages': 'Discover-Seiten pro Dienst (0 = alle)',
  'tmdb.start': 'Sync starten',
  'tmdb.alreadyRunning': 'Es läuft schon ein Sync.',
  'tmdb.startFailed': 'Der Sync ließ sich nicht starten.',
  'tmdb.invalidLimit': 'Bitte eine ganze Zahl zwischen 0 und {max} eingeben.',
  'stats.recordingOff':
    'Das Zählen ist abgeschaltet (USAGE_STATS=off). Die Bestandszahlen weiter unten stimmen weiterhin; die Zähler stehen nur still.',
  'stats.visits': 'Seitenaufrufe im Zeitraum',
  'stats.todayValue': 'Heute: {count}',
  'stats.percent': '{value} %',
  'stats.appShare': 'Aufrufe aus der installierten App',
  'stats.appVersusWeb': 'App {app} · Browser {web}',
  'stats.sessionsCreated': 'Angelegte Runden',
  'stats.startedValue': 'Davon gestartet: {count}',
  'stats.filmsChosen': 'Filme, auf die sich eine Gruppe geeinigt hat',
  'stats.matchesValue': 'Matches: {count}',
  'stats.newDevices': 'Neue Geräte',
  'stats.newDevicesHint': 'Browser, die zum ersten Mal eine Gerätekennung bekommen haben.',
  'stats.newAccounts': 'Neue Konten',
  'stats.accountsTotal': '{count} insgesamt',
  'stats.perDayTitle': 'Tag für Tag',
  'stats.perDayHint':
    'Der Balken hinter dem Datum sind die Seitenaufrufe des Tages, im Verhältnis zum stärksten Tag der Liste. Ein Punkt heißt: nichts gezählt.',
  'stats.day': 'Tag',
  'stats.countersTitle': 'Alle Zähler',
  'stats.countersHint':
    'Namen wie gespeichert. „?“ ist eine Anfrage, deren Client nicht gesagt hat, woraus sie kam — ein Crawler, ein Skript oder eine Seite aus dem Cache von vor dieser Änderung.',
  'stats.counter': 'Zähler',
  'stats.total': 'Zeitraum',
  'stats.today': 'Heute',
  'stats.empty': 'In diesem Zeitraum ist noch nichts gezählt worden.',
  'stats.standingTitle': 'Bestandszahlen',
  'stats.standingHint':
    'Direkt aus den Tabellen gezählt: sie gelten für die ganze Laufzeit und nicht nur für den Zeitraum oben.',
  'stats.standingDevices': 'Bekannte Geräte',
  'stats.standingAccounts': 'Konten, bestätigt von gesamt',
  'stats.standingFilms': 'Filme, Community von gesamt',
  'stats.standingClaims': 'Fakten-Beiträge',
  'stats.standingWatchlist': 'Merkzettel-Einträge',
  'stats.standingRatings': 'Bewertungen',
  'stats.standingLiveSessions': 'Gerade laufende Runden',
  'stats.confirmedOf': '{confirmed} von {total}',
  'stats.communityOf': '{community} von {total}',
  'stats.footer': '{from} bis {to}, Tage in UTC. Abgerufen {time}.',

  'ads.label': 'Anzeige',
  'ads.placeholder': 'Hier würde eine Anzeige stehen.',

  // Filmwerkstatt: ein offener Fakt nach dem anderen — ausfüllen, bestätigen,
  // eine strittige Frage entscheiden, oder einen alten Streaming-Fakt prüfen.
  'workshop.title': 'Filmwerkstatt',
  'workshop.subtitle': 'Fakten zu Filmen prüfen und bestätigen — einer nach dem anderen.',
  'workshop.empty': 'Es gibt gerade nichts zu klären.',
  'workshop.emptyHint': 'Schau später nochmal vorbei, wenn neue Fakten offen sind.',
  'workshop.openPoints': '{count} offene Punkte',
  'workshop.unlockable': '{count} Filme fast startklar',
  'workshop.contributionsConfirmed': '{count} bestätigte Beiträge',
  'workshop.contributionsOpen': '{count} offene Beiträge',
  'workshop.skip': 'Überspringen',
  'workshop.openRecord': 'Filmakte öffnen',
  'workshop.loginRequired': 'Zum Mitmachen brauchst du ein Konto.',
  'workshop.loginCta': 'Anmelden oder registrieren',
  'workshop.position': '{current} von {total}',
  'workshop.typeFill': 'Ausfüllen',
  'workshop.typeConfirm': 'Bestätigen',
  'workshop.typeDecide': 'Entscheiden',
  'workshop.typeRefresh': 'Auffrischen',
  // Kontextzeile unter dem Filmtitel, aus den Zahlen vom Server gebaut:
  // "1966 · 161 Min. · 2 Genres · 1 Anbieter".
  'workshop.summaryEmpty': 'Noch nichts bestätigt',
  'workshop.summaryGenre': '1 Genre',
  'workshop.summaryGenres': '{count} Genres',
  'workshop.summaryProvider': '1 Streaming-Dienst',
  'workshop.summaryProviders': '{count} Streaming-Dienste',
  'workshop.fieldYear': 'Jahr',
  'workshop.fieldRuntime': 'Länge',
  'workshop.fieldGenre': 'Genre',
  'workshop.fieldProvider': 'Streaming-Dienst',
  'workshop.askYear': 'Stimmt das Jahr?',
  'workshop.askRuntime': 'Stimmt die Länge?',
  'workshop.askGenre': 'Passt das Genre {genre}?',
  'workshop.askProvider': 'Läuft der Film bei {provider}?',
  'workshop.askRefresh': 'Läuft der Film hier noch?',
  'workshop.yes': 'Ja',
  'workshop.no': 'Nein',
  'workshop.correct': 'Stimmt',
  'workshop.incorrectWithValue': 'Stimmt nicht — richtiger Wert:',
  'workshop.unsure': 'Unsicher',
  'workshop.submit': 'Absenden',
  'workshop.enterValue': 'Wert eingeben',
  'workshop.noNeedsAlternative': 'Ein bloßes Nein hilft niemandem. Sag uns, was stimmt.',
  'workshop.oneSupporter': 'Bisher nur eine Stimme.',
  'workshop.supportersNeeded': '{needed} weitere nötig',
  'workshop.stillValid': 'Noch aktuell',
  'workshop.lastChecked': 'Zuletzt geprüft vor {days} Tagen',
  'workshop.unlocksFilm': 'Damit wird der Film startklar.',
  'workshop.creditEarned': '+{days} Tage werbefrei',
  'workshop.stillRuns': 'Läuft noch hier',
  'workshop.gone': 'Nicht mehr hier',

  // Filmakte: die Seite zu einem Film, ein Eintrag pro Fakt.
  'record.eyebrow': 'Filmakte',
  'record.deckReady': 'Startklar',
  'record.notDeckReady': 'Noch nicht startklar',
  'record.provisional': 'Verfügbarkeit ungeprüft',
  'record.openPoints': '{count} offene Punkte',
  'record.addGenre': 'Genre ergänzen',
  'record.addProvider': 'Streaming-Dienst ergänzen',
  'record.stateConfirmed': 'Bestätigt',
  'record.stateProposed': 'Vorgeschlagen',
  'record.stateDisputed': 'Strittig',
  'record.stateStale': 'Veraltet',
  'record.lockedBy': 'Von {name} festgelegt',
  'record.lockedAnonymous': 'Von einem Moderator festgelegt',
  'record.lock': 'Festlegen',
  'record.unlock': 'Freigeben',
  'record.reopen': 'Neu prüfen lassen',
  'record.stateOpen': 'Offen',
  'record.votes': '{count} Stimmen',
  'record.youAnsweredYes': 'Du hast mit Ja gestimmt. Warten auf weitere Bestätigung.',
  'record.youAnsweredNo': 'Du hast mit Nein gestimmt. Warten auf weitere Bestätigung.',
  'record.youAnsweredValue': 'Du hast {value} angegeben. Warten auf weitere Bestätigung.',
  'record.year': 'Jahr',
  'record.runtime': 'Länge',
  'record.genres': 'Genres',
  'record.providers': 'Streaming-Dienste',
  'record.trailer': 'Trailer',
  'record.trailerNotYet': 'Ein Trailer kann ergänzt werden, sobald dieser Film deckbereit ist.',
  'record.trailerAddSuccess': 'Danke! Der Trailer ist jetzt für alle sichtbar.',
  'record.trailerRemoved': 'Für alle entfernt. Jetzt kann jemand den richtigen ergänzen.',
  'record.trailerReviewOnly':
    'Vermerkt — dieser Trailer wurde von uns direkt hinzugefügt, daher muss ihn jemand von uns prüfen, bevor er entfernt wird.',
  'record.trailerCounted':
    'Vermerkt. Wenn noch ein paar mehr das Gleiche sagen, wird er für alle entfernt.',
  'record.trailerReportFailed': 'Das hat nicht geklappt. Bitte gleich noch mal versuchen.',
  'record.blocked': 'Blockiert, bis das hier geklärt ist: {missing}.',
  'record.ready': 'Bereit für den Stapel.',

  // Film hinzufügen: nur der Titel ist Pflicht. Alles andere wird ein
  // offener Punkt für die Filmwerkstatt.
  'film.addTitle': 'Film hinzufügen',
  'film.addHint':
    'Trag den Titel ein. Jahr, Länge, Genres und Anbieter klärt die Filmwerkstatt danach.',
  'film.titleLabel': 'Titel',
  'film.titleRequired': 'Der Titel ist das Einzige, was du eingeben musst.',
  'film.optional': 'Optional',
  'film.optionalHint': 'Der Rest ist optional — die Filmwerkstatt klärt ihn mit der Zeit.',
  'film.year': 'Jahr',
  'film.runtime': 'Länge',
  'film.genres': 'Genres',
  'film.providers': 'Streaming-Dienste',
  'film.submit': 'Absenden',
  'film.submitting': 'Wird gesendet…',
  'film.success': 'Film hinzugefügt.',
  'film.duplicate': 'Diesen Film gibt es schon.',
  'film.footnote': 'Ein Konto brauchst du dafür nicht.',
  'film.cancel': 'Abbrechen',

  // Einen Film beim Swipen vorschlagen: erst suchen, damit niemand einen
  // Titel doppelt anlegt, der bereits in der Datenbank ist — und der
  // Gewinner wird als Nächstes zur Abstimmung gestellt.
  'film.suggestTitle': 'Film vorschlagen',
  'film.suggestHint': 'Durchsuch den Katalog und leg der ganzen Gruppe einen Film vor.',
  'film.searching': 'Suche läuft…',
  'film.suggestAction': 'Vorschlagen',
  'film.suggestPending': 'Schon vorgeschlagen — wartet noch auf Bestätigung durch die Community.',
  'film.suggestNoResults':
    'Nichts gefunden. Im Katalog sind nur Filme, die auf einem der unterstützten Dienste laufen.',
  'film.suggestAddNew': 'Hinzufügen',
  'film.suggestNotListed': 'Nicht der richtige?',
  'film.suggestAddNewTitled': '"{title}" als neuen Film hinzufügen',
  'film.suggestCreateHint':
    'Die Gruppe stimmt als Nächstes darüber ab. Der Titel reicht — alles andere kann später in der Filmwerkstatt ergänzt werden.',
  'film.suggestCreateRequired': 'Ein Titel ist Pflicht.',
  'film.suggestBackToSearch': 'Zurück zur Suche',
  'film.suggestSuccess': 'Vorgeschlagen! Er ist jetzt für alle als Nächstes dran.',

  // Warum ein Vorschlag abgelehnt wurde. „Steht schon im Deck“ gehört nicht
  // dazu: Das Deck enthält jeden Titel, den die Filter zulassen — so ein Film
  // wird einfach nach vorn geholt.
  'suggestError.already_voted': 'Alle hier haben diesen Film schon bewertet.',
  'suggestError.not_swiping': 'Die Runde nimmt gerade keine Stimmen an.',
  'suggestError.film_not_found': 'Diesen Film gibt es in der Datenbank nicht mehr.',
  'suggestError.unknown': 'Hat nicht geklappt. Bitte versuch es noch einmal.',

  // Startseite: Teaser für Filmwerkstatt und Konto.
  'home.workshop': 'Filmwerkstatt',
  'home.workshopHint': '{count} offene Punkte warten',
  'home.workshopLocked': 'Mitmachen braucht ein Konto.',
  'home.workshopOpen': 'Zur Filmwerkstatt',
  'home.workshopSignIn': 'Anmelden und mitmachen',
  'home.signIn': 'Anmelden',
  'home.adFreeActive': 'Werbefrei bis {date}',
  'home.deviceSettings': 'App-Einstellungen',

  // Die App auf den Startbildschirm legen.
  'install.title': 'Leg sie auf deinen Startbildschirm',
  'install.body':
    'Öffnet ohne Browserleiste, und der Code ist beim nächsten Mal einen Tipp entfernt.',
  'install.iosBody': 'Tipp auf „Teilen“ und dann auf „Zum Home-Bildschirm“.',
  'install.action': 'Installieren',
  'install.dismiss': 'Jetzt nicht',
  'device.install': 'Zum Startbildschirm hinzufügen',
  'device.installHint': 'Öffnet ohne Browserleiste.',
  'device.installed': 'Auf diesem Gerät schon installiert.',

  // Einstellungen, die zu diesem Gerät gehören und nicht zum Konto.
  'device.title': 'Auf diesem Gerät',
  'device.intro':
    'Das hier sind Antworten über dieses Handy oder Tablet. Sie bleiben hier und stehen nie im Konto — ein anderes Gerät darf anders antworten.',
  'device.haptics': 'Vibration',
  'device.hapticsHint': 'Ein kurzes Brummen beim Abstimmen, ein längeres beim Treffer.',
  'device.hapticsUnsupported':
    'Dieser Browser kann nicht vibrieren. Auf iPhone und iPad kann das kein Browser.',
  'device.keepAwake': 'Bildschirm anlassen',
  'device.keepAwakeHint':
    'Solange eine Runde läuft, damit der Bildschirm zwischen zwei Karten nicht dunkel wird.',
  'device.keepAwakeUnsupported':
    'Dieser Browser kann den Bildschirm nicht anlassen. Es gilt deine übliche Bildschirmsperre.',
  'device.motionTitle': 'Bewegung',
  'device.motionReduced':
    'Dein System bittet um weniger Bewegung, deshalb sind Animationen aus. Ändern kannst du das in den Systemeinstellungen.',
  'device.motionFull':
    'Animationen richten sich nach deiner Systemeinstellung. Hier gibt es nichts einzustellen.',

  // Impressum, nach § 5 DDG gesetzlich vorgeschrieben.
  'legal.lead':
    'Wer diese Website betreibt, wie er zu erreichen ist, und die Angaben, die § 5 DDG dafür verlangt.',
  'legal.title': 'Impressum',
  'legal.responsibleFor': 'Verantwortlich für den Inhalt dieser Website nach § 5 DDG:',
  'legal.contact': 'Kontakt',
  'legal.email': 'E-Mail',

  // Seitenweite Navigation. Diese vier Seiten haben eigene Adressen (siehe
  // lib/routes.ts) und verlinken sich gegenseitig im Fuß jeder Seite.
  'nav.label': 'Seitennavigation',
  'nav.home': 'Startseite',
  'nav.about': 'Über uns',
  'nav.contact': 'Kontakt',
  'nav.privacy': 'Datenschutz',
  'nav.legal': 'Impressum',
  'nav.attribution': 'Filmdaten: TMDB · Streaming-Daten: JustWatch',

  // Was Suchmaschinen und geteilte Links anzeigen. Inhaltsseiten bauen ihren
  // Titel aus `meta.pageTitle` und nehmen ihren ersten Satz als Beschreibung.
  'meta.title': 'FilmSloth — gemeinsam entscheiden, was ihr heute Abend schaut',
  'meta.description':
    'FilmSloth ist die kostenlose Filmauswahl für die ganze Gruppe: Alle wischen durch dasselbe Deck, ein Nein nimmt einen Film raus, und der erste Titel, gegen den niemand etwas hat, gewinnt.',
  'meta.pageTitle': '{page} — FilmSloth',

  // Über uns. Pflichtlektüre für alle, die entscheiden, ob sie dieser Seite
  // trauen — und die Seite, die kleinen Websites am häufigsten fehlt.
  'about.title': 'Über uns',
  'about.lead':
    'FilmSloth ist eine kleine, unabhängige Seite, mit der sich eine Gruppe in Minuten auf einen Film einigt statt in einem halben Abend.',

  'about.whatTitle': 'Was die Seite macht',
  'about.whatBody1':
    'Eine Person eröffnet eine Runde, wählt die Streamingdienste aus, die alle haben, und teilt einen sechsstelligen Code. Danach wischt jede und jeder auf dem eigenen Handy durch dasselbe gemischte Deck und antwortet mit Ja, Egal oder Nein. Ein Nein nimmt den Film für die ganze Gruppe raus, ein Ja oder ein Egal lässt ihn im Rennen. Der erste Titel, gegen den niemand etwas hatte, ist der Treffer — und die Gruppe schaut ihn oder sucht weiter.',
  'about.whatBody2':
    'Es gibt nichts zu installieren, und für das Mitmachen braucht niemand ein Konto. Alles läuft im Browser, auf dem Handy genauso wie auf Tablet und Rechner.',

  'about.whoTitle': 'Wer dahintersteckt',
  'about.whoBody1':
    'FilmSloth wird von einer einzigen Person betrieben: Johannes Mahl aus Frankfurt am Main. Angefangen hat es als Lösung für einen Streit, der sich ständig wiederholte — vier Leute, drei Streamingdienste, zwanzig Minuten Scrollen und immer noch kein Film. Daraus ist die Seite geworden, über die du gerade liest.',
  'about.whoBody2':
    'Hinter der Seite steht kein Studio, kein Streamingdienst und kein Verlag, und niemand kann sich einkaufen, um einen Film im Deck weiter nach vorn zu bringen. Name und Anschrift stehen vollständig im Impressum.',

  'about.dataTitle': 'Woher die Filmdaten kommen',
  'about.dataBody1':
    'Jeder Film im Deck kommt aus The Movie Database (TMDB), einer von ihrer Community gepflegten Filmdatenbank: Titel, Jahr, Laufzeit, Genres, Beschreibung, Poster und Trailer. Auf welchen Streamingdiensten ein Film in Deutschland läuft, liefert JustWatch, weitergereicht über TMDB. FilmSloth aktualisiert das alles täglich automatisch; von Hand eingetragen wird nichts mehr.',
  'about.dataBody2':
    'Streamingkataloge ändern sich täglich, deshalb kann ein Film gerade erst aus einem Dienst verschwunden sein. Wenn etwas falsch aussieht, sag uns Bescheid — oder korrigiere es direkt an der Quelle auf themoviedb.org, dann kommt es bei allen an, die die Daten nutzen.',

  'about.moneyTitle': 'Wie die Seite finanziert wird',
  'about.moneyBody1':
    'FilmSloth kostet nichts und zeigt keine Werbung. Der Betrieb kostet trotzdem Geld — Server, Domain, Datenbank —, und das trägt die Person hinter der Seite, unterstützt von freiwilligen Spenden.',
  'about.moneyBody2':
    'Niemand kann sich einkaufen, um einen Film im Deck weiter nach vorn zu bringen, und über dich wird nichts verkauft oder an Werbetreibende weitergegeben.',
  'about.creditsTitle': 'Filmdaten und Quellen',
  'about.creditsTmdb': 'This product uses the TMDB API but is not endorsed or certified by TMDB.',
  'about.creditsTmdbBody':
    'Filmtitel, Beschreibungen, Poster, Trailer, Bewertungen und Erscheinungsdaten stellt TMDB (themoviedb.org) nach seinen API-Nutzungsbedingungen bereit. FilmSloth ist ein nicht-kommerzielles Projekt.',
  'about.creditsJustWatch': 'Die Daten zur Streaming-Verfügbarkeit stammen von JustWatch.',
  'about.creditsTmdbLink': 'The Movie Database (TMDB)',
  'about.creditsJustWatchLink': 'JustWatch',

  'about.contactTitle': 'Kontakt aufnehmen',
  'about.contactBody':
    'Fragen, Korrekturen, Fehlermeldungen und Presseanfragen sind willkommen. Auf der Kontaktseite stehen die Adresse und was dich erwartet.',

  // Kontakt. Die zweite Seite, nach der AdSense sucht — und die darüber
  // entscheidet, ob ein falscher Filmfakt je bei uns ankommt.
  'contact.title': 'Kontakt',
  'contact.lead':
    'Eine Adresse erreicht die Person, die FilmSloth betreibt — für Fragen, Korrekturen, Fehlermeldungen und alles Rechtliche.',

  'contact.emailTitle': 'Per E-Mail',
  'contact.emailBody':
    'E-Mail ist der schnellste Weg zu uns und der einzige Kanal, in den täglich jemand schaut. Schreib gern auf Deutsch oder Englisch.',

  'contact.topicsTitle': 'Worum es gehen kann',
  'contact.topicBug':
    'Etwas ist kaputt: was du gemacht hast, auf welchem Gerät, und was stattdessen passiert ist.',
  'contact.topicFilm':
    'Ein Filmeintrag stimmt nicht, ein Trailer zeigt das falsche Video, oder ein Titel fehlt ganz.',
  'contact.topicAccount':
    'Probleme beim Anmelden, eine Bestätigungsmail, die nie ankam, oder eine Löschung.',
  'contact.topicPrivacy':
    'Datenschutz: Auskunft über deine Daten, Berichtigung, Löschung oder Widerspruch gegen die Nutzung.',
  'contact.topicAds': 'Presse und alles andere, was die Seite als Ganzes betrifft.',

  'contact.responseTitle': 'Wann eine Antwort kommt',
  'contact.responseBody':
    'FilmSloth betreibt eine Person neben einem Hauptberuf. Das meiste ist innerhalb weniger Tage beantwortet; alles zum Datenschutz erledigen wir innerhalb des einen Monats, den die DSGVO einräumt, in aller Regel deutlich schneller.',

  'contact.postalTitle': 'Per Post',
  'contact.postalBody':
    'Post erreicht uns unter der folgenden Anschrift, derselben wie im Impressum.',

  // Datenschutz. Verlangt das deutsche Recht — und die AdSense-Richtlinien:
  // Wer Werbung ausliefert, muss sagen, was die mit Besuchern macht.
  'privacy.title': 'Datenschutz',
  'privacy.lead':
    'Was FilmSloth über dich speichert, warum, wer es sonst noch sieht und wie du es wieder loswirst.',
  'privacy.updated': 'Stand: {date}',

  'privacy.controllerTitle': 'Wer verantwortlich ist',
  'privacy.controllerBody':
    'Verantwortlicher im Sinne der DSGVO für die hier beschriebene Verarbeitung ist:',

  'privacy.hostingTitle': 'Beim Aufruf der Seite',
  'privacy.hostingBody1':
    'Die Seite läuft auf Servern der Render Services, Inc. in Frankfurt am Main. Beim Aufruf schickt dein Browser eine IP-Adresse, den Zeitpunkt, die angeforderte Seite und eine Browserkennung; der Hoster hebt das kurz auf, um den Dienst zu betreiben und abzusichern. Rechtsgrundlage ist unser berechtigtes Interesse an einer funktionierenden, angriffssicheren Seite (Art. 6 Abs. 1 lit. f DSGVO).',
  'privacy.hostingBody2':
    'Eine Analysesoftware von Dritten setzen wir nicht ein. Kein Google Analytics, kein Zählpixel, und über deinen Besuch geht nichts an ein anderes Unternehmen — auch die Filmposter holt unser Server bei TMDB ab und reicht sie an dich weiter, dein Browser spricht also nie mit TMDB. Was FilmSloth selbst zählt, steht vollständig im nächsten Abschnitt.',

  // Die eigene Zählung. Der Abschnitt existiert, weil der Satz darüber früher
  // schlicht „Eine Analysesoftware setzen wir nicht ein“ lautete — und das
  // stimmte ab dem Tag nicht mehr, an dem die Zähler live gingen. Beschrieben
  // ist genau das, was `UsageCounter` in schema.prisma und `lib/usage.ts` im
  // Client tun, und nichts darüber hinaus.
  'privacy.usageTitle': 'Wie wir zählen, wie viel genutzt wird',
  'privacy.usageBody1':
    'FilmSloth zählt auf dem eigenen Server mit, wie viel los ist. Gezählt werden Dinge wie „eine Seite wurde geöffnet“, „eine Runde wurde angelegt“, „eine Gruppe hat sich auf einen Film geeinigt“ — und ob die Seite in der installierten App oder in einem Browser-Tab geöffnet wurde. Gespeichert wird dazu jeweils ein Datum, der Name der Sache und eine Zahl. Sonst nichts. Es gibt keinen Eintrag zu deinem Besuch, keine Adresse, keine Browserkennung, keine Reihenfolge der Seiten; nichts an diesen Zahlen lässt sich auf dich zurückführen, und es ist auch nichts darin, woraus ein Profil würde.',
  'privacy.usageBody2':
    'Auf deinem Gerät wird dafür nichts gespeichert und nichts ausgelesen. Die Zählung setzt kein Cookie und liest keine Kennung, deshalb greift § 25 TMDDG — die Vorschrift, die vorher eine Einwilligung verlangt — hier gar nicht. Rechtsgrundlage ist unser berechtigtes Interesse daran, überhaupt zu wissen, ob die Seite genutzt wird (Art. 6 Abs. 1 lit. f DSGVO). Damit niemand die Zahlen einfach hochtreiben kann, hält der Server für eine Stunde einen Zähler pro Internetadresse im Arbeitsspeicher; der wird nicht protokolliert und nicht zu den Summen gespeichert.',
  'privacy.usageBody3':
    'Die Zahlen sind für uns. Sie stehen auf einer Seite, die ein Moderatorenkonto verlangt, und werden weder weitergegeben noch verkauft.',

  'privacy.storageTitle': 'Cookies und lokale Speicherung',
  'privacy.storageBody':
    'Eigene Werbecookies setzt FilmSloth nicht. Gespeichert wird das, was die Seite überhaupt funktionieren lässt:',
  'privacy.storageSession':
    'Ein Cookie „sessionId“: eine anonyme, zufällige Gerätekennung, damit dein Merkzettel und deine Bewertungen auch ohne Konto deine bleiben. Es läuft nach 30 Tagen ab.',
  'privacy.storageLogin':
    'Ein Cookie „auth“, erst nach der Anmeldung: Es hält dich angemeldet und verschwindet beim Abmelden.',
  'privacy.storageLocale':
    'Deine Sprachwahl und deine Antwort auf die Trailerfrage im lokalen Speicher des Browsers, damit beides nicht noch einmal gefragt werden muss.',
  'privacy.storageRound':
    'Code und Token der Runde, in der du gerade bist, im lokalen Speicher — damit ein Neuladen dich nicht rauswirft.',
  'privacy.storageBody2':
    'Diese Cookies sind für die von dir gewünschten Funktionen unbedingt erforderlich (§ 25 Abs. 2 TMDDG, Art. 6 Abs. 1 lit. f DSGVO). Wenn du deine Browserdaten löschst, sind sie alle weg; die Seite behandelt dich danach wie beim ersten Besuch.',

  'privacy.accountTitle': 'Wenn du ein Konto anlegst',
  'privacy.accountBody1':
    'Zu einem Konto gehören ein Benutzername, eine E-Mail-Adresse und dein Passwort als nicht umkehrbarer Hash, dazu die Filme, die du bewertet, gemerkt oder als gesehen markiert hast. Die E-Mail-Adresse gibt es, damit das Konto bestätigt und wiederhergestellt werden kann; verkauft, vermietet oder weitergegeben wird sie nie. Rechtsgrundlage ist der Vertrag, den du mit der Registrierung schließt (Art. 6 Abs. 1 lit. b DSGVO).',
  'privacy.accountBody2': 'Auf Zuruf löschen wir das Konto samt allem Persönlichen daran.',

  'privacy.adsTitle': 'Werbung',
  'privacy.adsBody1':
    'FilmSloth zeigt Werbung über Google AdSense, einen Dienst der Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland. Google setzt Cookies und vergleichbare Kennungen ein, um Anzeigen auszuliefern, ihre Sichtbarkeit zu messen und sie — je nach deinen Einstellungen und deiner Einwilligung — auf dich zuzuschneiden. Dabei können Daten die EU verlassen; Google stützt diese Übermittlungen auf die Standardvertragsklauseln der EU-Kommission.',
  'privacy.adsBody2':
    'Was Google verwenden darf, entscheidest du. Personalisierte Werbung lässt sich jederzeit in den Anzeigeneinstellungen von Google abschalten, und die Einwilligung für diese Seite kannst du dort ebenso ändern oder zurücknehmen. Werbung verschwindet dadurch nicht — sie ist dann nur nicht mehr personalisiert.',
  'privacy.adsBody3':
    'Beiträge in der Filmwerkstatt bringen werbefreie Tage, in denen gar keine Anzeige angefordert wird.',
  'privacy.adsSettingsLink': 'Anzeigeneinstellungen von Google',
  'privacy.adsPolicyLink': 'Wie Google Daten aus Anzeigen verwendet',

  'privacy.trailersTitle': 'Trailer von YouTube',
  'privacy.trailersBody':
    'Trailer laufen im Player von YouTube, und bis zum ersten Klick auf Abspielen wird bei Google überhaupt nichts angefragt — weder das Video noch sein Vorschaubild. Danach erfährt YouTube deine IP-Adresse und kann Cookies setzen; wir benutzen dafür den datenschutzfreundlicheren Host youtube-nocookie.com. Deine Antwort merkt sich dein Browser, und du kannst sie auf jeder Filmseite wieder zurücknehmen.',
  'privacy.tmdbTitle': 'Filmdaten von TMDB',
  'privacy.tmdbBody':
    'Filminformationen und Poster stammen aus The Movie Database (TMDB, TMDB Inc., USA), die Streaming-Verfügbarkeit von JustWatch über TMDB. Unser Server ruft diese Daten und die Posterbilder selbst ab und reicht sie an dich weiter. Dein Browser baut keine Verbindung zu TMDB oder JustWatch auf, und über dich wird an keinen der beiden etwas übermittelt.',

  'privacy.rightsTitle': 'Deine Rechte',
  'privacy.rightsBody':
    'Du kannst jederzeit und kostenlos Folgendes von uns verlangen. Eine kurze Mail an die Kontaktadresse genügt:',
  'privacy.rightsAccess':
    'Auskunft über die Daten, die wir zu dir haben, und darüber, was wir damit tun.',
  'privacy.rightsRectify': 'Berichtigung von allem, was nicht stimmt.',
  'privacy.rightsErase': 'Löschung deines Kontos und der persönlichen Daten daran.',
  'privacy.rightsRestrict': 'Einschränkung der Verarbeitung oder Widerspruch dagegen.',
  'privacy.rightsPortability': 'Deine Daten in einem übertragbaren, maschinenlesbaren Format.',
  'privacy.rightsComplain':
    'Eine Beschwerde bei einer Aufsichtsbehörde — für uns ist das der Hessische Beauftragte für Datenschutz und Informationsfreiheit —, wenn du findest, dass wir das hier falsch machen.',

  'privacy.retentionTitle': 'Wie lange etwas bleibt',
  'privacy.retentionBody':
    'Eine Runde und ihr Deck werden wenige Stunden nach der letzten Person gelöscht. Kontodaten bleiben, bis du das Konto löschst. Technische Protokolle hebt der Hoster nur so lange auf, wie er sie für Betrieb und Absicherung braucht.',

  'privacy.changesTitle': 'Änderungen an dieser Seite',
  'privacy.changesBody':
    'Wenn sich ändert, was die Seite mit Daten macht, ändert sich diese Seite mit. Das Datum oben sagt, wann das zuletzt der Fall war.',

  // Genre names. The ids live in `shared`; only the wording is per locale.
  'genre.action': 'Action',
  'genre.adventure': 'Abenteuer',
  'genre.animation': 'Animation',
  'genre.comedy': 'Komödie',
  'genre.crime': 'Krimi',
  'genre.documentary': 'Dokumentation',
  'genre.drama': 'Drama',
  'genre.family': 'Familie',
  'genre.fantasy': 'Fantasy',
  'genre.history': 'Historie',
  'genre.horror': 'Horror',
  'genre.music': 'Musik',
  'genre.mystery': 'Mystery',
  'genre.romance': 'Liebesfilm',
  'genre.science-fiction': 'Science-Fiction',
  'genre.thriller': 'Thriller',
  'genre.war': 'Kriegsfilm',
  'genre.western': 'Western',
};
